'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import BrandMark from './BrandMark';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [sessionUser, setSessionUser] = useState<{ name?: string; email?: string } | null>(null);
  const vendorDashboardUrl = process.env.NEXT_PUBLIC_VENDOR_DASHBOARD_URL;
  const vendorHomeUrl = vendorDashboardUrl ? `${vendorDashboardUrl.replace(/\/$/, '')}/dashboard` : '/login';
  const vendorLoginUrl = vendorDashboardUrl ? `${vendorDashboardUrl.replace(/\/$/, '')}/login` : '/login';
  const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;

  const navigationItems = [
    { name: 'Pricing', path: '/pricing' },
    { name: 'How it works', path: '/how-it-works' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
  ];

  const productWorkspaces = [
    ['AI Chat', 'Website agents and lead capture', '/products/ai-chat-bot'],
    ['WhatsApp', 'Templates, campaigns, and replies', '/products/whatsapp-automation'],
    ['CustomerGraph', 'Consent-based customer profiles', '/products/customergraph'],
    ['CRM', 'Leads, contacts, and timelines', '/products/crm'],
    ['Analytics', 'Channels, sources, and conversion', '/products/analytics'],
    ['Email Marketing', 'Verified sender campaigns', '/products/email-marketing'],
    ['Support', 'Tickets and product help', '/products/support'],
  ];

  const combinations = [
    ['Website leads', 'AI Chat + CRM'],
    ['WhatsApp + sales', 'WhatsApp + CRM'],
    ['Full stack', 'AI Chat + WhatsApp + CRM + Analytics'],
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const tokenKey = 'advika_vendor_access_token';

    const unwrapResponseData = (payload: any) => {
      if (!payload?.status) return null;
      return payload.data?.status && payload.data?.data ? payload.data.data : payload.data;
    };

    const extractAccessToken = (payload: any) => {
      const data = unwrapResponseData(payload);
      return data?.accessToken || payload?.data?.accessToken || null;
    };

    const fetchCurrentUser = async (token: string) => {
      if (!apiBaseUrl) return null;

      const response = await fetch(`${apiBaseUrl.replace(/\/$/, '')}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.status) return null;

      return unwrapResponseData(payload);
    };

    const refreshAccessToken = async () => {
      if (!apiBaseUrl) return null;

      const response = await fetch(`${apiBaseUrl.replace(/\/$/, '')}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      const payload = await response.json().catch(() => null);
      const accessToken = extractAccessToken(payload);
      if (!response.ok || !accessToken) return null;

      window.localStorage.setItem(tokenKey, accessToken);
      return accessToken as string;
    };

    const applyUser = (user: any) => {
      if (!user) {
        setSessionUser(null);
        return;
      }

      setSessionUser({
        name: user.name || user.fullName || user.email,
        email: user.email,
      });
    };

    const loadSession = async () => {
      if (typeof window === 'undefined') return;
      if (!apiBaseUrl) {
        setSessionUser(null);
        return;
      }

      try {
        let token = window.localStorage.getItem(tokenKey);
        if (!token) {
          applyUser(null);
          return;
        }
        let user = token ? await fetchCurrentUser(token) : null;

        if (!user) {
          token = await refreshAccessToken();
          user = token ? await fetchCurrentUser(token) : null;
        }

        applyUser(user);
      } catch {
        setSessionUser(null);
      }
    };

    loadSession();

    const handleStorage = (event: StorageEvent) => {
      if (event.key === tokenKey) loadSession();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [apiBaseUrl]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 border-b border-border/80 bg-background/90 shadow-[0_10px_30px_rgba(15,23,42,0.06)] backdrop-blur-xl ${className}`}>
      <div className="mx-auto w-[min(94vw,1520px)] px-1">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center">
            <BrandMark />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <div
              className="relative"
              onMouseEnter={() => setIsProductsOpen(true)}
              onMouseLeave={() => setIsProductsOpen(false)}
            >
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-foreground transition-all duration-200 hover:bg-muted"
                onClick={() => setIsProductsOpen((value) => !value)}
                aria-expanded={isProductsOpen}
              >
                Products
                <Icon name="ChevronDownIcon" size={17} />
              </button>

              {isProductsOpen ? (
                <div className="absolute left-1/2 top-full w-[min(86vw,660px)] -translate-x-1/2 pt-2">
                  <div className="rounded-2xl border border-border bg-white p-5 text-left shadow-[0_18px_54px_rgba(15,23,42,0.15)]">
                  <div className="grid gap-7 md:grid-cols-[1fr_0.85fr]">
                    <div>
                      <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                        Product workspaces
                      </p>
                      <div className="space-y-3">
                        {productWorkspaces.map(([name, description, path]) => (
                          <Link
                            key={name}
                            href={path}
                            className="group grid grid-cols-[12px_1fr] gap-3 rounded-lg p-1 transition hover:bg-slate-50"
                          >
                            <span className="mt-2 h-2 w-2 rounded-full bg-[#4F46E5] transition group-hover:scale-125" />
                            <span>
                              <span className="block text-[15px] font-semibold leading-tight text-slate-950">{name}</span>
                              <span className="mt-0.5 block text-xs leading-5 text-slate-500">{description}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                        Popular combinations
                      </p>
                      <div className="space-y-4">
                        {combinations.map(([name, products]) => (
                          <Link key={name} href="/products" className="block rounded-xl p-1 transition hover:bg-slate-50">
                            <span className="block text-[15px] font-semibold leading-tight text-slate-950">{name}</span>
                            <span className="mt-0.5 block text-xs leading-5 text-slate-500">{products}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>

                  <Link
                    href="/products"
                    className="mt-5 flex items-center gap-2 border-t border-slate-200 pt-3 text-sm font-semibold text-[#4F46E5] hover:text-[#312E81]"
                  >
                    View all products
                    <Icon name="ArrowRightIcon" size={16} />
                  </Link>
                  </div>
                </div>
              ) : null}
            </div>

            {navigationItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                className="rounded-xl px-5 py-3 text-sm font-semibold text-muted-foreground transition-all duration-200 hover:bg-muted hover:text-foreground"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Session */}
          <div className="hidden items-center justify-end gap-3 lg:flex">
            {sessionUser ? (
              <Link
                href={vendorHomeUrl}
                className="inline-flex items-center gap-3 rounded-xl border border-border bg-white px-3 py-2 text-sm font-semibold text-foreground shadow-sm transition hover:border-primary/30 hover:text-primary"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[linear-gradient(135deg,#312E81,#4F46E5_55%,#6D5EF4)] text-xs font-bold text-white">
                  {(sessionUser.name || sessionUser.email || 'A').slice(0, 1).toUpperCase()}
                </span>
                <span className="max-w-[150px] truncate">{sessionUser.name || 'Dashboard'}</span>
              </Link>
            ) : (
              <>
                <Link
                  href={vendorLoginUrl}
                  className="rounded-xl px-4 py-2 text-sm font-semibold text-[#4F46E5] transition hover:bg-[#F5F3FF] hover:text-[#312E81]"
                >
                  Sign In
                </Link>
                <Link
                  href="/contact"
                  className="inline-flex h-10 items-center justify-center rounded-xl bg-[linear-gradient(135deg,#312E81,#4F46E5_55%,#6D5EF4)] px-5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(79,70,229,0.22)] transition hover:-translate-y-0.5"
                >
                  Book Demo
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-foreground hover:text-primary transition-colors duration-200"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur">
            <nav className="py-4 space-y-1">
              <Link
                href="/products"
                className="block px-4 py-3 text-base font-medium text-muted-foreground hover:text-primary hover:bg-muted transition-all duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Products
              </Link>
              <div className="px-4 pb-2">
                <div className="rounded-2xl border border-border bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Product workspaces</p>
                  <div className="mt-3 space-y-3">
                    {productWorkspaces.map(([name, description, path]) => (
                      <Link
                        key={name}
                        href={path}
                        className="grid grid-cols-[12px_1fr] gap-3"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="mt-2 h-2 w-2 rounded-full bg-[#4F46E5]" />
                        <span>
                          <span className="block text-sm font-semibold text-foreground">{name}</span>
                          <span className="block text-xs leading-5 text-muted-foreground">{description}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  className="block px-4 py-3 text-base font-medium text-muted-foreground hover:text-primary hover:bg-muted transition-all duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              {sessionUser ? (
                <div className="px-4 pt-4 pb-2 border-t border-border mt-2">
                  <Link
                    href={vendorHomeUrl}
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-primary px-4 py-3 text-center text-base font-semibold text-primary transition-all duration-200 hover:bg-primary/5"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-xs font-bold text-white">
                      {(sessionUser.name || sessionUser.email || 'A').slice(0, 1).toUpperCase()}
                    </span>
                    {sessionUser.name || 'Dashboard'}
                  </Link>
                </div>
              ) : (
                <div className="px-4 pt-4 pb-2 space-y-3 border-t border-border mt-2">
                  <Link
                    href={vendorLoginUrl}
                    className="block w-full rounded-md border border-primary px-4 py-3 text-center text-base font-semibold text-primary transition-all duration-200 hover:bg-primary/5"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/contact"
                    className="block w-full rounded-md bg-primary px-4 py-3 text-center text-base font-semibold text-primary-foreground transition-all duration-200 hover:bg-primary/90"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Book Demo
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
